import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Linking, TextInput } from 'react-native';
import { useFocusEffect, useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../../src/api/client';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import ContentContainer from '../../../src/components/ContentContainer';
import { useToast } from '../../../src/components/Toast';
import { useTheme } from '../../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import type { ColorTheme } from '../../../src/utils/theme';
import { titleCase, formatPhone, formatDate } from '../../../src/utils/format';
import { formatDateTime } from '../../../src/utils/timezone';

interface CustomerDetail {
  profile: {
    userId: string; name: string | null; email: string | null; phone: string | null; emailVerified: boolean; customerSince: string | null;
    signupSource?: string | null; signupVoucherCode?: string | null; signupVoucherId?: string | null;
  };
  stats: { totalWashes: number; lifetimeSpend: number; washesThisMonth: number; lastSeenAt: string | null; lastSeenLocal: string | null; avgRating: number | null; ratingCount: number };
  activeSubscriptions: Array<{ planName: string; tierName: string; maxWashesPerDay: number; maxWashesPerMonth: number }>;
  activeBundles: Array<{ bundleName: string; tierName: string; washesRemaining: number; washesTotal: number }>;
  recentActivity: Array<{ id: string; kind: string; amount: number; createdAt: string | null; status: string | null; refundedAmount: number; tierName: string | null; locationName: string | null; bayName: string | null }>;
  promosUsed?: Array<{ id: string; redeemedAt: string | null; discountAmountCents: number; code: string; kind: string; discountKind: string; percentOff: number | null; tierName: string | null; freeMinutes: number | null; source: string | null }>;
  note: string | null;
}

/** Human label for a used promo/gift row. */
function promoLabel(p: NonNullable<CustomerDetail['promosUsed']>[number]): string {
  if (p.discountKind === 'credit') return 'Gift credit';
  if (p.discountKind === 'free_vend') return 'Vending credit';
  if (p.discountKind === 'free_upsell') return 'Free add-on';
  if (p.discountKind === 'free_minutes') return `${p.freeMinutes ?? '?'} free minutes`;
  if (p.discountKind === 'free_wash' || p.percentOff === 100) return p.tierName ? `Free ${p.tierName} wash` : 'Free wash';
  return `${p.percentOff}% off${p.tierName ? ` ${p.tierName}` : ''}`;
}

export default function OwnerCustomerDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const router = useRouter();

  const [data, setData] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [noteDirty, setNoteDirty] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  const fetchDetail = useCallback(async () => {
    try {
      const d = await api<CustomerDetail>(`/api/customers/${id}`);
      setData(d);
      setNote(d.note ?? '');
      setNoteDirty(false);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setLoading(false); }
  }, [id]);

  useFocusEffect(useCallback(() => { fetchDetail(); }, [fetchDetail]));

  const fmtMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await api(`/api/customers/${id}/note`, { method: 'PUT', body: { note } });
      setNoteDirty(false);
      toast.show({ type: 'success', title: 'Note saved' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setSavingNote(false); }
  };

  if (loading) return <View style={styles.loading}><Text style={{ color: colors.textSecondary }}>Loading…</Text></View>;
  if (!data) return <View style={styles.loading}><Text style={{ color: colors.textSecondary }}>Customer not found.</Text></View>;

  const { profile, stats } = data;
  const e = profile.email ? encodeURIComponent(profile.email) : null;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer maxWidth={720}>

        {/* Profile */}
        <Card style={{ marginBottom: spacing.md }}>
          <Text style={styles.name}>{titleCase(profile.name) || '(no name)'}</Text>
          {profile.email && (
            <TouchableOpacity onPress={() => Linking.openURL(`mailto:${profile.email}`)}>
              <Text style={styles.contact}>{profile.email}{profile.emailVerified ? '' : '  (unverified)'}</Text>
            </TouchableOpacity>
          )}
          {profile.phone && (
            <TouchableOpacity onPress={() => Linking.openURL(`tel:${profile.phone}`)}>
              <Text style={styles.contact}>{formatPhone(profile.phone)}</Text>
            </TouchableOpacity>
          )}
          {profile.customerSince && <Text style={styles.since}>Customer since {formatDate(profile.customerSince)}</Text>}
          {profile.signupVoucherCode && (
            <TouchableOpacity onPress={() => profile.signupVoucherId && router.push(`/owner/subscriptions/vouchers/${profile.signupVoucherId}` as never)}>
              <Text style={[styles.since, { color: colors.primary }]}>Signed up via QR code {profile.signupVoucherCode}</Text>
            </TouchableOpacity>
          )}
          {!profile.signupVoucherCode && profile.signupSource === 'fleet_invite' && (
            <Text style={styles.since}>Signed up via fleet invite</Text>
          )}
        </Card>

        {/* Lifetime stats */}
        <Card style={{ marginBottom: spacing.md }}>
          <View style={styles.statGrid}>
            <Stat label="This month" value={String(stats.washesThisMonth)} c={colors} />
            <Stat label="Lifetime washes" value={String(stats.totalWashes)} c={colors} />
            <Stat label="Lifetime spend" value={fmtMoney(stats.lifetimeSpend)} c={colors} />
            <Stat label="Rating" value={stats.avgRating != null ? `${stats.avgRating.toFixed(1)} (${stats.ratingCount})` : '—'} c={colors} />
            <Stat label="Last seen" value={formatDate(stats.lastSeenLocal)} c={colors} />
          </View>
        </Card>

        {/* Plans / bundles */}
        {(data.activeSubscriptions.length > 0 || data.activeBundles.length > 0) && (
          <Card style={{ marginBottom: spacing.md }}>
            <Text style={styles.sectionTitle}>Active plans</Text>
            {data.activeSubscriptions.map((s, i) => (
              <View key={`s${i}`} style={styles.planRow}>
                <Ionicons name="ribbon" size={16} color={colors.primary} />
                <Text style={styles.planText}>{s.planName}{s.tierName ? ` · ${s.tierName}` : ''}</Text>
              </View>
            ))}
            {data.activeBundles.map((b, i) => (
              <View key={`b${i}`} style={styles.planRow}>
                <Ionicons name="cube" size={16} color={colors.success} />
                <Text style={styles.planText}>{b.bundleName} · {b.washesRemaining}/{b.washesTotal} left</Text>
              </View>
            ))}
          </Card>
        )}

        {/* Quick actions */}
        <Card style={{ marginBottom: spacing.md }}>
          <Text style={styles.sectionTitle}>Actions</Text>
          <View style={styles.actionRow}>
            <Button title="Gift / Promo" variant="outline" icon={<Ionicons name="gift" size={16} color={colors.primary} />}
              onPress={() => e ? router.push(`/owner/subscriptions/gifts/new?email=${e}` as never) : toast.show({ type: 'error', title: 'No email on file' })} />
            <Button title="$ Credit" variant="outline" icon={<Ionicons name="cash" size={16} color={colors.primary} />}
              onPress={() => e ? router.push(`/owner/subscriptions/gifts/new?kind=credit&email=${e}` as never) : toast.show({ type: 'error', title: 'No email on file' })} />
            {profile.email && (
              <Button title="Email" variant="outline" icon={<Ionicons name="mail" size={16} color={colors.primary} />}
                onPress={() => Linking.openURL(`mailto:${profile.email}`)} />
            )}
          </View>
        </Card>

        {/* Owner note */}
        <Card style={{ marginBottom: spacing.md }}>
          <Text style={styles.sectionTitle}>Private note</Text>
          <TextInput
            style={styles.noteInput}
            placeholder="Notes only you can see (e.g. preferences, issues)…"
            placeholderTextColor={colors.textLight}
            value={note}
            onChangeText={(t) => { setNote(t); setNoteDirty(true); }}
            multiline
            maxLength={2000}
          />
          {noteDirty && (
            <Button title={savingNote ? 'Saving…' : 'Save note'} onPress={saveNote} disabled={savingNote} style={{ marginTop: spacing.sm }} />
          )}
        </Card>

        {/* Promos & gifts used */}
        {(data.promosUsed?.length ?? 0) > 0 && (
          <Card style={{ marginBottom: spacing.md }}>
            <Text style={styles.sectionTitle}>Promos & gifts used</Text>
            {data.promosUsed!.map(p => (
              <View key={p.id} style={styles.activityRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.activityTitle}>{promoLabel(p)}</Text>
                  <Text style={styles.activitySub}>
                    Code {p.code}{p.redeemedAt ? ` · ${formatDateTime(p.redeemedAt)}` : ''}
                  </Text>
                </View>
                {p.discountAmountCents > 0 && (
                  <Text style={styles.activityAmount}>-{fmtMoney(p.discountAmountCents)}</Text>
                )}
              </View>
            ))}
          </Card>
        )}

        {/* Recent activity */}
        <Card style={{ marginBottom: spacing.md }}>
          <Text style={styles.sectionTitle}>Recent activity</Text>
          {data.recentActivity.length === 0 && <Text style={styles.empty}>No activity yet.</Text>}
          {data.recentActivity.map(a => {
            const refunded = a.refundedAmount > 0;
            return (
              <View key={`${a.kind}-${a.id}`} style={styles.activityRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.activityTitle}>
                    {a.tierName || (a.kind === 'wash_session' ? 'Self-serve wash' : 'Wash')}
                    {a.locationName ? ` · ${a.locationName}` : ''}
                  </Text>
                  <Text style={styles.activitySub}>
                    {a.createdAt ? formatDateTime(a.createdAt) : ''}{a.bayName ? ` · ${a.bayName}` : ''}
                  </Text>
                </View>
                <View style={{ alignItems: 'flex-end' }}>
                  <Text style={[styles.activityAmount, refunded && { textDecorationLine: 'line-through', color: colors.textLight }]}>{fmtMoney(a.amount)}</Text>
                  {refunded && <Text style={styles.refundTag}>refunded {fmtMoney(a.refundedAmount)}</Text>}
                </View>
              </View>
            );
          })}
        </Card>

        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

function Stat({ label, value, c }: { label: string; value: string; c: ColorTheme }) {
  return (
    <View style={{ minWidth: 90, marginBottom: spacing.sm }}>
      <Text style={{ fontSize: 10, color: c.textLight, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</Text>
      <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold as any, color: c.text, marginTop: 1 }}>{value}</Text>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  name: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  contact: { fontSize: fontSize.sm, color: c.primary, marginTop: 4 },
  since: { fontSize: fontSize.xs, color: c.textLight, marginTop: spacing.sm },
  statGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.lg, rowGap: spacing.sm },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm },
  planRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingVertical: 4 },
  planText: { fontSize: fontSize.sm, color: c.text },
  actionRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  noteInput: { minHeight: 80, borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md, padding: spacing.sm, fontSize: fontSize.sm, color: c.text, textAlignVertical: 'top' },
  activityRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm, borderTopWidth: 1, borderTopColor: c.borderLight, gap: spacing.sm },
  activityTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  activitySub: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  activityAmount: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  refundTag: { fontSize: 10, color: c.danger, marginTop: 1 },
  empty: { fontSize: fontSize.sm, color: c.textLight, paddingVertical: spacing.sm },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
